import React, { useCallback, useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import { MapView } from './components/MapView';
import { CardCarousel } from './components/CardCarousel';
import { recommendations, Category } from './components/data';
interface StampConfig {
  src: string;
  finalRotate: number;
  stackRotate: number;
  stackOffset: {
    x: number;
    y: number;
  };
  delay: string;
  // Which corner: tl, tr, bl, br
  corner: 'tl' | 'tr' | 'bl' | 'br';
  // Offsets from the map container edge (px) — mirrors the old Tailwind classes
  edgeOffset: {
    x: number;
    y: number;
  };
  // Responsive sizes (width in px at each breakpoint)
  width: {
    base: 96;
    md: 144;
    lg: 176;
  } | {
    base: 112;
    md: 160;
    lg: 192;
  } | {
    base: 96;
    md: 144;
    lg: 160;
  } | {
    base: 80;
    md: 112;
    lg: 128;
  };
}
const STAMPS: StampConfig[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/7dRckPAdc6uJDYSEgmARP8/Stamp-1.png',
  finalRotate: -4,
  stackRotate: -8,
  stackOffset: {
    x: -14,
    y: -14
  },
  delay: '0ms',
  corner: 'tl',
  edgeOffset: {
    x: -24,
    y: -16
  },
  width: {
    base: 96,
    md: 144,
    lg: 176
  }
}, {
  src: 'https://cdn.magicpatterns.com/uploads/k6bCdt7aGsaVy5vYWgZbtR/Stamp.png',
  finalRotate: 3,
  stackRotate: 5,
  stackOffset: {
    x: 14,
    y: -14
  },
  delay: '55ms',
  corner: 'tr',
  edgeOffset: {
    x: -16,
    y: -16
  },
  width: {
    base: 112,
    md: 160,
    lg: 192
  }
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3eoRKeye9pgZnadoUY6Xzs/Stamp-2.png',
  finalRotate: 2,
  stackRotate: 12,
  stackOffset: {
    x: -14,
    y: 14
  },
  delay: '30ms',
  corner: 'bl',
  edgeOffset: {
    x: -20,
    y: 8
  },
  width: {
    base: 96,
    md: 144,
    lg: 160
  }
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jJjBjQ6ea9LpGTqdwTJ48o/Stamp-3.png',
  finalRotate: -3,
  stackRotate: -6,
  stackOffset: {
    x: 14,
    y: 14
  },
  delay: '75ms',
  corner: 'br',
  edgeOffset: {
    x: -12,
    y: 16
  },
  width: {
    base: 80,
    md: 112,
    lg: 128
  }
}];
function getStampWidth(stamp: StampConfig): number {
  if (typeof window === 'undefined') return stamp.width.base;
  const w = window.innerWidth;
  if (w >= 1024) return stamp.width.lg;
  if (w >= 768) return stamp.width.md;
  return stamp.width.base;
}
/** Calculate the fixed top/left for a stamp's corner position relative to the map rect */
function getCornerPosition(stamp: StampConfig, mapRect: DOMRect): {
  top: number;
  left: number;
} {
  const stampW = getStampWidth(stamp);
  // Assume roughly square aspect ratio for stamps
  const stampH = stampW;
  switch (stamp.corner) {
    case 'tl':
      return {
        top: mapRect.top + stamp.edgeOffset.y,
        left: mapRect.left + stamp.edgeOffset.x
      };
    case 'tr':
      return {
        top: mapRect.top + stamp.edgeOffset.y,
        left: mapRect.right + stamp.edgeOffset.x - stampW
      };
    case 'bl':
      return {
        top: mapRect.bottom - stampH + stamp.edgeOffset.y,
        left: mapRect.left + stamp.edgeOffset.x
      };
    case 'br':
      return {
        top: mapRect.bottom - stampH + stamp.edgeOffset.y,
        left: mapRect.right + stamp.edgeOffset.x - stampW
      };
  }
}
export function App() {
  const [activeCategory, setActiveCategory] = useState<Category | 'ALL'>('ALL');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [phase, setPhase] = useState<'idle' | 'scattered'>('idle');
  const [mapRect, setMapRect] = useState<DOMRect | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  // Drag state
  const [dragPositions, setDragPositions] = useState<Record<number, {
    top: number;
    left: number;
  }>>({});
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const dragOffset = useRef({
    x: 0,
    y: 0
  });
  const measureMap = useCallback(() => {
    if (mapRef.current) {
      setMapRect(mapRef.current.getBoundingClientRect());
    }
  }, []);
  useLayoutEffect(() => {
    measureMap();
    window.addEventListener('resize', measureMap);
    return () => window.removeEventListener('resize', measureMap);
  }, [measureMap]);
  useEffect(() => {
    const t = setTimeout(() => setPhase('scattered'), 1100);
    return () => clearTimeout(t);
  }, []);
  const scattered = phase === 'scattered';
  // Global mousemove/mouseup for dragging
  useEffect(() => {
    if (draggingIndex === null) return;
    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      setDragPositions(prev => ({
        ...prev,
        [draggingIndex]: {
          top: e.clientY - dragOffset.current.y,
          left: e.clientX - dragOffset.current.x
        }
      }));
    };
    const handleMouseUp = () => {
      setDraggingIndex(null);
    };
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      setDragPositions(prev => ({
        ...prev,
        [draggingIndex]: {
          top: touch.clientY - dragOffset.current.y,
          left: touch.clientX - dragOffset.current.x
        }
      }));
    };
    const handleTouchEnd = () => {
      setDraggingIndex(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, {
      passive: false
    });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [draggingIndex]);
  const handleStampPointerDown = (i: number, e: React.MouseEvent | React.TouchEvent) => {
    if (!scattered) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    dragOffset.current = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
    setDraggingIndex(i);
  };
  const filteredItems = useMemo(() => {
    if (activeCategory === 'ALL') return recommendations;
    return recommendations.filter(i => i.category === activeCategory);
  }, [activeCategory]);
  const handleCategoryChange = (cat: Category | 'ALL') => {
    setActiveCategory(cat);
    if (selectedId) {
      const item = recommendations.find(i => i.id === selectedId);
      if (item && cat !== 'ALL' && item.category !== cat) setSelectedId(null);
    }
  };
  // Viewport center for idle position
  const vcx = typeof window !== 'undefined' ? window.innerWidth / 2 : 500;
  const vcy = typeof window !== 'undefined' ? window.innerHeight / 2 : 400;
  return <div className="relative flex flex-col w-screen h-screen overflow-hidden bg-white text-gray-900" data-id="element-0">
      {/* White screen shown during idle — stamps sit on top of this */}
      <div style={{
      position: 'fixed',
      inset: 0,
      background: 'white',
      zIndex: 10,
      opacity: scattered ? 0 : 1,
      pointerEvents: scattered ? 'none' : 'all',
      transition: 'opacity 0.7s ease 0.3s'
    }} data-id="element-1" />

      {/* ── STAMPS (fixed position, above everything) ── */}
      {STAMPS.map((stamp, i) => {
      const stampW = getStampWidth(stamp);
      const cornerPos = mapRect ? getCornerPosition(stamp, mapRect) : null;
      // Idle: centered on viewport
      const idleTop = vcy - stampW / 2 + stamp.stackOffset.y;
      const idleLeft = vcx - stampW / 2 + stamp.stackOffset.x;
      // Scattered: at corner of map container
      const targetTop = cornerPos ? cornerPos.top : idleTop;
      const targetLeft = cornerPos ? cornerPos.left : idleLeft;
      // If user has dragged this stamp, use that position
      const customPos = dragPositions[i];
      const finalTop = customPos ? customPos.top : scattered ? targetTop : idleTop;
      const finalLeft = customPos ? customPos.left : scattered ? targetLeft : idleLeft;
      const idleTransform = `rotate(${stamp.stackRotate}deg) scale(0.65)`;
      const doneTransform = `rotate(${stamp.finalRotate}deg)`;
      const isDragging = draggingIndex === i;
      return <img key={i} src={stamp.src} alt="" draggable={false} className="stamp-img" onMouseDown={e => handleStampPointerDown(i, e)} onTouchStart={e => handleStampPointerDown(i, e)} style={{
        position: 'fixed',
        top: finalTop,
        left: finalLeft,
        width: stampW,
        transform: scattered ? doneTransform : idleTransform,
        transitionDelay: isDragging || customPos ? '0ms' : stamp.delay,
        filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.13))',
        zIndex: isDragging ? 30 : 20,
        cursor: scattered ? isDragging ? 'grabbing' : 'grab' : 'default',
        pointerEvents: scattered ? 'auto' : 'none',
        // Disable transitions while dragging for instant feedback
        transition: isDragging ? 'none' : undefined
      }} data-id="element-2" />;
    })}

      {/* ── MAIN CONTENT ── */}
      <div className="flex flex-col h-full w-full max-w-[1200px] mx-auto px-4 md:px-8 lg:px-32" data-id="element-3">
        {/* Title */}
        <div className="pt-5 md:pt-8 pb-3 md:pb-4 shrink-0" style={{
        opacity: scattered ? 1 : 0,
        transform: scattered ? 'translateY(0)' : 'translateY(14px)',
        transition: 'opacity 0.9s ease 1s, transform 0.9s cubic-bezier(0.22,1,0.36,1) 1s'
      }} data-id="element-4">
          <h1 className="text-2xl md:text-3xl font-medium text-gray-900 tracking-tight" style={{
          fontFamily: "'DM Sans', system-ui, sans-serif"
        }} data-id="element-5">
            JP's Ottawa Recommendations
          </h1>
        </div>

        {/* ── MAP ── */}
        <div ref={mapRef} className="relative w-full h-[38vh] md:h-[44vh] shrink-0" data-id="element-6">
          <div className="stamp-edge bg-white p-2.5 md:p-3 h-full" style={{
          filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.08)) drop-shadow(0 1px 3px rgba(0,0,0,0.06))',
          opacity: scattered ? 1 : 0,
          transform: scattered ? 'scale(1)' : 'scale(0.97)',
          transition: 'opacity 1s ease 0.7s, transform 1s cubic-bezier(0.22,1,0.36,1) 0.7s'
        }} data-id="element-7">
            <div className="relative w-full h-full rounded-sm overflow-hidden" data-id="element-8">
              {selectedId && <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000]" data-id="element-9">
                  <button onClick={() => setSelectedId(null)} className="bg-white/90 backdrop-blur-md px-4 py-2.5 rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] border border-gray-100 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors italic" data-id="element-10">
                    Zoom out
                  </button>
                </div>}
              <MapView items={filteredItems} selectedId={selectedId} onSelect={setSelectedId} data-id="element-11" />
            </div>
          </div>
        </div>

        {/* Card carousel */}
        <div className="flex-1 w-full z-10 relative bg-white" style={{
        opacity: scattered ? 1 : 0,
        transform: scattered ? 'translateY(0)' : 'translateY(18px)',
        transition: 'opacity 0.9s ease 1.1s, transform 0.9s cubic-bezier(0.22,1,0.36,1) 1.1s'
      }} data-id="element-12">
          <CardCarousel items={filteredItems} activeCategory={activeCategory} onCategoryChange={handleCategoryChange} selectedId={selectedId} onSelect={setSelectedId} data-id="element-13" />
        </div>
      </div>
    </div>;
}